import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Card, StatusPill, PriorityPill, AtomBadge, EmptyState } from "../components/primitives";
import { formatNGN } from "../lib/format";

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState({ status: "", priority: "" });
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/alerts").then(r => {
      setAlerts(r.data.alerts || []);
      setSelected(r.data.alerts?.[0] || null);
    });
  }, []);

  const filtered = alerts.filter(a => (!filter.status || a.status === filter.status) && (!filter.priority || a.priority === filter.priority));

  return (
    <div>
      <PageHeader
        title="Alerts"
        subtitle={`${filtered.length} alerts in queue`}
        sections={["5.5","5.7"]}
        atom={<AtomBadge model="Atom Proton" version="v2.4" />}
      />
      <div className="grid grid-cols-[320px_1fr_320px] h-[calc(100vh-150px)]">
        {/* Queue */}
        <div className="border-r border-line bg-bg-surface overflow-y-auto">
          <div className="px-3 py-2 border-b border-line text-[11px] flex items-center gap-1 flex-wrap">
            <span className="text-ink-subtle">Priority:</span>
            {["","P1","P2","P3","P4"].map(p => (
              <button key={p} onClick={() => setFilter({...filter, priority: p})} className={`px-1.5 py-0.5 rounded-sm border ${filter.priority===p?'bg-navy text-white border-navy':'border-line'}`}>{p || "All"}</button>
            ))}
          </div>
          {filtered.map(a => (
            <button key={a.id} data-testid={`alert-queue-${a.id}`} onClick={() => setSelected(a)} className={`w-full text-left px-3 py-2.5 border-b border-line/50 hover:bg-bg-hover ${selected?.id === a.id ? 'bg-amber/5 border-l-2 border-l-amber' : ''}`}>
              <div className="flex items-start gap-2">
                <PriorityPill priority={a.priority} />
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-medium truncate">{a.subject_name}</div>
                  <div className="text-[10px] text-ink-subtle truncate">{a.rule}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <StatusPill status={a.status} />
                    <span className="text-[10px] text-ink-subtle">{a.id}</span>
                  </div>
                </div>
              </div>
            </button>
          ))}
          {filtered.length === 0 && <EmptyState message="No alerts in queue." />}
        </div>

        {/* Detail */}
        <div className="overflow-y-auto p-4">
          {selected ? (
            <>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <PriorityPill priority={selected.priority} />
                    <StatusPill status={selected.status} />
                    <span className="text-[11px] text-ink-subtle">{selected.id}</span>
                  </div>
                  <h2 className="text-[18px] font-medium">{selected.subject_name}</h2>
                  <div className="text-[12px] text-ink-subtle">{selected.rule} · Raised {selected.raised}</div>
                </div>
                <Link to={`/alerts/${selected.id}`} className="px-3 py-1.5 text-[12px] bg-navy text-white rounded-sm hover:bg-navy-700">Open full view →</Link>
              </div>

              <Card className="mb-3">
                <div className="px-4 py-3 border-b border-line text-[12px] font-medium">Implicated activity</div>
                <div className="p-3 text-[12px] space-y-1">
                  <div className="flex justify-between"><span className="text-ink-subtle">Amount at risk</span><span className="font-medium tabular">{formatNGN(selected.amount_at_risk)}</span></div>
                  <div className="flex justify-between"><span className="text-ink-subtle">Description</span><span className="text-right max-w-[60%]">{selected.description}</span></div>
                  <div className="flex justify-between"><span className="text-ink-subtle">Assignee</span><span>{selected.assignee}</span></div>
                  <div className="flex justify-between"><span className="text-ink-subtle">CBN sections</span><span>{selected.cbn.join(", ")}</span></div>
                </div>
              </Card>

              <Card>
                <div className="px-4 py-3 border-b border-line flex items-center justify-between">
                  <div className="text-[12px] font-medium">Atom narrative</div>
                  <AtomBadge model="Atom Nucleus" version="v3.5" />
                </div>
                <div className="p-4 text-[12px] leading-relaxed border-l-2 border-amber/40 bg-amber/4 ml-4 my-4">
                  This alert flags {selected.subject_name} for activity matching AMLTRIX {selected.typology}. Atom Proton v2.4 raised the alert at {selected.raised} with model confidence ≥0.85. The amount at risk on the implicated transaction window is {formatNGN(selected.amount_at_risk)}. Pattern is consistent with the typology and recommended next action is MLRO escalation.
                  <div className="text-[10px] text-ink-subtle mt-2">CBN {selected.cbn.join(' · ')} · FATF Rec. 20 · Explainability 0.91</div>
                </div>
              </Card>

              <div className="flex gap-2 mt-4">
                <button className="px-3 py-1.5 text-[12px] bg-navy text-white rounded-sm">Create case</button>
                <button className="px-3 py-1.5 text-[12px] border border-line rounded-sm hover:bg-bg-hover">Reassign</button>
                <button className="px-3 py-1.5 text-[12px] border border-line rounded-sm hover:bg-bg-hover">Escalate</button>
                <button className="px-3 py-1.5 text-[12px] border border-line rounded-sm hover:bg-bg-hover">Add note</button>
                <button className="px-3 py-1.5 text-[12px] border border-risk-high/40 text-risk-high rounded-sm hover:bg-risk-high/8">Clear</button>
              </div>
            </>
          ) : <EmptyState message="Select an alert from the queue." />}
        </div>

        {/* Context */}
        <div className="border-l border-line bg-bg-surface overflow-y-auto p-3">
          {selected && (
            <>
              <div className="text-[11px] font-medium text-ink-subtle uppercase tracking-wider mb-2">Subject customer</div>
              <Card className="mb-3">
                <div className="p-3">
                  <Link to={`/customers/${selected.subject_customer}`} className="text-[13px] font-medium text-amber-600 hover:underline">{selected.subject_name}</Link>
                  <div className="text-[11px] text-ink-subtle mt-0.5">{selected.subject_customer}</div>
                </div>
              </Card>
              <div className="text-[11px] font-medium text-ink-subtle uppercase tracking-wider mb-2">Recent alerts · same customer</div>
              <Card className="mb-3"><div className="p-3 text-[11px] text-ink-muted">2 prior alerts in last 6 months; both cleared as false positives after MLRO review.</div></Card>
              <div className="text-[11px] font-medium text-ink-subtle uppercase tracking-wider mb-2">Suggested actions · Atom Nucleus</div>
              <Card><div className="p-3 text-[11px] space-y-2">
                <div>· Request source-of-funds documentation</div>
                <div>· Run cross-account network analysis</div>
                <div>· Consider STR drafting</div>
                <AtomBadge model="Atom Nucleus" version="v3.5" />
              </div></Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
